import { useEffect } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { NextUpPlayer } from "./Components/NextUpPlayer";
import {
  PlaylistIdState,
  PlaylistLayoutState,
} from "MVVM/State/embeddablePlaylist";
import { TrackedFirstHoverState } from "MVVM/State/iframe";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import { TRNPlaylist } from "./Components/TRNPlaylist";
import { HLTVEmbeddablePlaylist } from "./Components/HLTVEmbeddablePlaylist";
import { OPGGEmbeddablePlaylist } from "./Components/OPGGEmbeddablePlaylist";
import { GenericEmbeddablePlaylist } from "./Components/GenericEmbeddablePlaylist";
import { BrandedEmbeddablePlaylist } from "./Components/BrandedEmbeddablePlaylist";
import { UGGHighlightsTab } from "./Components/UGGHighlightsTab";

interface IEmbeddablePlaylistProps {
  playlist: string;
}

const EmbeddablePlaylist = ({ playlist }: IEmbeddablePlaylistProps) => {
  const searchParams = new URLSearchParams(window.location.search);
  const platform = searchParams.get("platform") || "";
  const layout = searchParams.get("layout") || "playlist";
  const theme = searchParams.get("theme") || "night";

  const [trackedFirstHover, setTrackedFirstHover] = useRecoilState(
    TrackedFirstHoverState,
  );
  const setPlaylistId = useSetRecoilState(PlaylistIdState);
  const [playlistLayout, setPlaylistLayout] =
    useRecoilState(PlaylistLayoutState);
  const { track } = useAnalyticsContext();

  useEffect(() => {
    if (!playlist) return;
    setPlaylistId(playlist);
  }, [playlist, setPlaylistId]);

  useEffect(() => {
    setPlaylistLayout(layout);
  }, [setPlaylistLayout, layout]);

  if (playlistLayout === "nextUp")
    return <NextUpPlayer playlist={playlist} platform={platform} />;

  const handleMouseEnter = () => {
    if (!trackedFirstHover) {
      track("Iframe First Hover", {
        locationIdentifier: "iframe_first_hover",
        location: "embeddable_playlist",
        platform,
      });
      setTrackedFirstHover(true);
    }
  };

  const platformMap: Record<string, string> = {
    TRACKERNETWORKLLC: "trn",
    UDOTGG: "ugg",
    "U.GG": "ugg",
    HLTV: "hltv",
    "HLTV.ORG": "hltv",
    "CROM, INC.": "hltv",
    "CROM,INC": "hltv",
    "CROM,INC.": "hltv",
    "OP.GG": "opgg",
  };

  const playlistType = platformMap[platform];
  const themeable = ["hltv"];

  // HLTV defaults to the 2026 branded iframe. Pass `?branded=0` to fall back
  // to the legacy HLTVEmbeddablePlaylist (kept around for quick A/B compares
  // and as an escape hatch if a partner reports a regression).
  const useLegacyHltv = searchParams.get("branded") === "0";

  const components = {
    trn: TRNPlaylist,
    ugg: NextUpPlayer,
    hltv: HLTVEmbeddablePlaylist,
    opgg: OPGGEmbeddablePlaylist,
  } as const;

  // Two URL flags override a platform's default component; everything else is the map.
  const resolveComponent = () => {
    // Opt-in: without ?layout=highlights-tab U.GG still resolves to NextUpPlayer.
    if (playlistType === "ugg" && layout === "highlights-tab")
      return UGGHighlightsTab;
    if (playlistType === "hltv" && !useLegacyHltv)
      return BrandedEmbeddablePlaylist;

    return (
      components[playlistType as keyof typeof components] ??
      GenericEmbeddablePlaylist
    );
  };

  const EmbeddablePlaylistComponent = resolveComponent();

  return (
    <EmbeddablePlaylistComponent
      platform={platform}
      playlist={playlist}
      handleMouseEnter={handleMouseEnter}
      theme={themeable.includes(playlistType) ? theme : undefined}
    />
  );
};

export default EmbeddablePlaylist;
